import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Chip } from "../../components/ui/chip";
import { ArrowLeft, FileText, ShieldAlert, CheckCircle2, Calendar, IndianRupee, History, ChevronLeft, Eye, Paperclip, CreditCard, ArrowLeftRight, Loader2, Download, Truck, Percent, Shield, Hash, Contact, Upload, Plus, Pencil } from "lucide-react";
import { format, parseISO, differenceInDays, addDays, addMonths, subDays, isValid } from "date-fns";
import { daysLeft, daysLeftBucket,  DUE_WINDOW_DAYS } from "../../lib/post-disbursement";
import { toast } from "react-toastify";
import type { Schedule } from "../../store/appStore";
import type { Asset } from "../../types/schema";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Progress } from "../../components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import SupplierShipmentsSection from "./supplierShipmentSection";
import apiService from "../../services/apiService";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { AssetDetailSidebar } from "../../components/purchaseOrder/AssetDetailSidebar";


const inrFmt = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

type OverviewApiPayload = {
  mca_charge_file?: { disbursement_date?: string | null } | null;
  nachData?: { noa_nach_start_date?: string | null } | null;
  tax_invoice?: { tax_invoice_count?: number | string | null } | null;
  eway?: {
    eway_count?: number | string | null;
    eway_uploaded_count?: number | string | null;
    eway_not_applicable_count?: number | string | null;
  } | null;
  total_invoices?: { total_invoices_count?: number | string | null } | null;
  gst_vendor?: { gst_vendor_count?: number | string | null } | null;
  total_assets?: { total_assets?: number | string | null } | null;
  insurance_covered_assets?: { total_insured_assets?: number | string | null } | null;
};

type OverviewChecklistStatus = "Pending" | "Filed" | "Active" | "Uploaded" | "Complete" | "Not Applicable";

type OverviewChecklistItem = {
  key: string;
  title: string;
  description: string;
  status: OverviewChecklistStatus;
  icon: ReactNode;
};

function extractOverviewPayload(response: unknown): OverviewApiPayload | null {
  if (!response || typeof response !== "object") return null;
  const root = response as Record<string, unknown>;
  const nested = root.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : null;
  const source = nested ?? root;
  if (
    "mca_charge_file" in source ||
    "nachData" in source ||
    "tax_invoice" in source ||
    "eway" in source ||
    "gst_vendor" in source ||
    "total_assets" in source
  ) {
    return source as OverviewApiPayload;
  }
  return null;
}

function buildOverviewChecklist(
  data: OverviewApiPayload | null,
  fallbackDisbursementDate?: string | null,
  hasActiveInsurance = false,
): OverviewChecklistItem[] {
  const mcaFiled = Boolean(data?.mca_charge_file);
  const mcaDisbursementDate = data?.mca_charge_file?.disbursement_date ?? fallbackDisbursementDate ?? null;
  const mcaDueDate = mcaDisbursementDate
    ? addDays(parseISO(String(mcaDisbursementDate).slice(0, 10)), DUE_WINDOW_DAYS)
    : null;

  const nachActive = Boolean(data?.nachData);
  const nachStartDate = data?.nachData?.noa_nach_start_date ?? null;

  const taxInvoiceCount = Number(data?.tax_invoice?.tax_invoice_count ?? 0);
  const ewayDone = Number(data?.eway?.eway_count ?? 0);
  const ewayUploaded = Number(data?.eway?.eway_uploaded_count ?? 0);
  const ewayNotApplicable = Number(data?.eway?.eway_not_applicable_count ?? 0);
  const totalInvoices = Number(data?.total_invoices?.total_invoices_count ?? 0);
  const gstVendorCount = Number(data?.gst_vendor?.gst_vendor_count ?? 0);
  const taxComplete = totalInvoices > 0 && taxInvoiceCount >= totalInvoices;
  const ewayStatus: OverviewChecklistStatus =
    totalInvoices > 0 && ewayNotApplicable === totalInvoices
      ? "Not Applicable"
      : totalInvoices > 0 && ewayDone === totalInvoices
        ? "Complete"
        : "Pending";
  const gstComplete = totalInvoices > 0 && gstVendorCount >= totalInvoices;

  const taxPct = totalInvoices > 0 ? Math.round((taxInvoiceCount / totalInvoices) * 100) : 0;
  const ewayPct = totalInvoices > 0 ? Math.round((ewayDone / totalInvoices) * 100) : 0;
  const gstPct = totalInvoices > 0 ? Math.round((gstVendorCount / totalInvoices) * 100) : 0;

  return [
    {
      key: "mca",
      title: "MCA Charge Filing (CHG-1)",
      description: mcaDueDate ? `Due ${format(mcaDueDate, "dd MMM yyyy")}` : "Due date unavailable",
      status: mcaFiled ? "Filed" : "Pending",
      icon: <FileText className="h-4 w-4" />,
    },
    {
      key: "nach",
      title: "NACH Mandate",
      description: nachActive
        ? nachStartDate
          ? `Active from ${format(parseISO(String(nachStartDate).slice(0, 10)), "dd MMM yyyy")}`
          : "Mandate registered"
        : "Mandate not registered",
      status: nachActive ? "Active" : "Pending",
      icon: <CreditCard className="h-4 w-4" />,
    },
    {
      key: "tax",
      title: "Tax Invoices",
      description: `${taxPct}% of PIs covered · ${taxInvoiceCount} of ${totalInvoices} PIs`,
      status: taxComplete ? "Uploaded" : "Pending",
      icon: <FileText className="h-4 w-4" />,
    },
    {
      key: "eway",
      title: "E-Way Bills",
      description: `${ewayPct}% of PIs covered · ${ewayDone} of ${totalInvoices} PIs (${ewayUploaded} uploaded, ${ewayNotApplicable} N/A)`,
      status: ewayStatus,
      icon: <Truck className="h-4 w-4" />,
    },
    {
      key: "gst",
      title: "GST — Vendor GSTR-1 & ITC",
      description: `${gstVendorCount} of ${totalInvoices} matched · ${gstPct}% of PIs ITC-ready`,
      status: gstComplete ? "Filed" : "Pending",
      icon: <Percent className="h-4 w-4" />,
    },
    {
      key: "insurance",
      title: "Asset Insurance",
      description: hasActiveInsurance
        ? "Active insurance policy on this rental schedule"
        : "No active insurance policy",
      status: hasActiveInsurance ? "Active" : "Pending",
      icon: <ShieldAlert className="h-4 w-4" />,
    },
  ];
}

function OverviewStatusChip({ status }: { status: OverviewChecklistStatus }) {
  if (status === "Pending") {
    return <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">Pending</Chip>;
  }
  if (status === "Not Applicable") {
    return <Chip className="bg-slate-500/10 text-slate-700 text-[10px] px-2 py-0.5">Not Applicable</Chip>;
  }
  return (
    <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      {status}
    </Chip>
  );
}

const getFileNameFromStoragePath = (path: string, fallback = "download") => {
  const name = path.split("/").pop()?.trim();
  return name || fallback;
};

const getMimeTypeFromFileName = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  return "application/octet-stream";
};

const getExtensionFromMimeType = (mimeType: string) => {
  if (mimeType === "application/pdf") return "pdf";
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/jpeg" || mimeType === "image/jpg") return "jpg";
  return null;
};

const hasFileExtension = (fileName: string) => /\.[a-z0-9]+$/i.test(fileName);

const ensureDownloadFileName = (fileName: string, fileRef: string, blobType?: string) => {
  if (hasFileExtension(fileName)) return fileName;

  const pathName = getFileNameFromStoragePath(fileRef);
  if (hasFileExtension(pathName)) return pathName;

  const extFromMime = blobType ? getExtensionFromMimeType(blobType) : null;
  if (extFromMime) return `${fileName}.${extFromMime}`;

  const ext = fileRef.split(".").pop()?.toLowerCase();
  if (ext === "pdf" || ext === "png" || ext === "jpg" || ext === "jpeg") {
    return `${fileName}.${ext === "jpeg" ? "jpg" : ext}`;
  }

  return fileName;
};

const resolveDownloadMimeType = (blob: Blob, fileName: string) => {
  const fromName = getMimeTypeFromFileName(fileName);
  if (fromName !== "application/octet-stream") return fromName;
  if (blob.type && !blob.type.includes("text/html") && blob.type !== "application/json") {
    return blob.type;
  }
  return "application/octet-stream";
};

const isAbsoluteFileUrl = (url: string) =>
  url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://");

const fundingModeLabel = (m: "A" | "B" | "C") => m === "A" ? "Pre-GST" : m === "B" ? "Post-GST" : "Post-TDS";

const extractScheduleId = (payload: unknown): string => {
  if (!payload || typeof payload !== "object") return "";
  const row = payload as Record<string, unknown>;
  return String(
    row.scheduleId ?? row.schedule_id ?? row.rentalScheduleId ?? row.rental_schedule_id ?? "",
  ).trim();
};

const extractRentalScheduleFromResponse = (response: any): Schedule | null => {
  const schedules = response?.schedules ?? response?.data?.schedules;
  if (Array.isArray(schedules) && schedules.length > 0) {
    return schedules[0] as Schedule;
  }
  const single = response?.schedule ?? response?.data?.schedule ?? response?.data;
  return single && typeof single === "object" && !Array.isArray(single) ? (single as Schedule) : null;
};

type AssetCategoryOption = { id: string; name: string };

const extractAssetCategories = (response: unknown): AssetCategoryOption[] => {
  const root = response as Record<string, unknown> | null | undefined;
  const payload = root?.data ?? response;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as Record<string, unknown>)?.categories)
      ? (payload as Record<string, unknown>).categories
      : Array.isArray((payload as Record<string, unknown>)?.rows)
        ? (payload as Record<string, unknown>).rows
        : [];

  return (list as Record<string, unknown>[])
    .map((item) => ({
      id: String(item.id ?? item.category_id ?? "").trim(),
      name: String(item.name ?? item.category_name ?? item.label ?? "").trim(),
    }))
    .filter((item) => item.id && item.name);
};

const extractInvoicesList = (response: unknown): any[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  const root = response as Record<string, unknown>;
  const payload = root.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const nested = payload as Record<string, unknown>;
    if (Array.isArray(nested.invoices)) return nested.invoices;
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.rows)) return nested.rows;
  }
  return [];
};

const extractPostDisbursementPayload = (response: any) => {
  const payload = response?.data ?? response;
  return (
    payload?.case ??
    payload?.details ??
    payload?.item ??
    (Array.isArray(payload?.rows) ? payload.rows[0] : undefined) ??
    (Array.isArray(payload) ? payload[0] : undefined) ??
    payload
  ) ?? null;
};

const extractCompliancePercent = (response: unknown): number => {
  const root = response as Record<string, unknown> | null | undefined;
  const payload = root?.data ?? response;
  const score = (payload as Record<string, unknown>)?.total_percentage_score
    ?? root?.total_percentage_score;
  const n = Number(score);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 0;
};

const toScheduleNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const optionalText = (value: unknown) => {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

type ScheduleInsurancePolicy = {
  id: string;
  start_date: string;
  end_date: string;
  tenure_months?: number;
  insurance_provider: string;
  premium: number;
  upload_policy_filename?: string;
  upload_policy_s3_fileurl?: string;
  upload_quotation_filename?: string;
  upload_quotation_s3_fileurl?: string;
  coordinator_name?: string;
  coordinator_email?: string;
  coordinator_phone?: string;
  policy_code?: string;
  remarks?: string;
  updated_at?: string;
};

const normalizeScheduleInsurancePolicy = (policy: Record<string, unknown>): ScheduleInsurancePolicy | null => {
  const start_date = String(policy.start_date ?? "").trim();
  const end_date = String(policy.end_date ?? "").trim();
  const id = String(policy.id ?? "").trim();
  if (!start_date || !end_date || !id) return null;

  return {
    id,
    start_date,
    end_date,
    tenure_months: policy.tenure_months != null ? toScheduleNumber(policy.tenure_months) : undefined,
    insurance_provider: String(policy.insurance_provider ?? "").trim(),
    premium: toScheduleNumber(policy.premium, 0),
    upload_policy_filename: optionalText(policy.upload_policy_filename),
    upload_policy_s3_fileurl: optionalText(policy.upload_policy_s3_fileurl),
    upload_quotation_filename: optionalText(policy.upload_quotation_filename),
    upload_quotation_s3_fileurl: optionalText(policy.upload_quotation_s3_fileurl),
    coordinator_name: optionalText(policy.coordinator_name),
    coordinator_email: optionalText(policy.coordinator_email),
    coordinator_phone: optionalText(policy.coordinator_phone),
    policy_code: optionalText(policy.policy_code),
    remarks: optionalText(policy.remarks),
    updated_at: optionalText(policy.updated_at),
  };
};

const normalizeScheduleInsurancePolicies = (response: unknown): ScheduleInsurancePolicy[] => {
  const root = response && typeof response === "object" ? response as Record<string, unknown> : null;
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(root?.data)
      ? root.data
      : [];
  return rows
    .filter((policy): policy is Record<string, unknown> => !!policy && typeof policy === "object")
    .map((policy) => normalizeScheduleInsurancePolicy(policy))
    .filter((policy): policy is ScheduleInsurancePolicy => policy != null);
};

const getAssetMatchIds = (asset: Asset) => {
  const assetRow = asset as Asset & Record<string, unknown>;
  return new Set(
    [asset.assetId, assetRow.id, assetRow.individual_asset_id, assetRow.asset_id]
      .filter((value) => value != null && String(value).trim())
      .map((value) => String(value).trim()),
  );
};

const formatPolicyDate = (value?: string | null, pattern = "dd MMM yyyy") => {
  if (!value) return "—";
  const parsed = parseISO(String(value).slice(0, 10));
  return isValid(parsed) ? format(parsed, pattern) : "—";
};

const formatCoverPeriod = (policy: ScheduleInsurancePolicy) => {
  const start = formatPolicyDate(policy.start_date);
  const end = formatPolicyDate(policy.end_date);
  if (start === "—" || end === "—") return "—";
  return `${start} to ${end}`;
};

const policyTenureMonths = (policy: ScheduleInsurancePolicy) => {
  if (policy.tenure_months != null && policy.tenure_months > 0) return policy.tenure_months;
  const start = parseISO(policy.start_date.slice(0, 10));
  const end = parseISO(policy.end_date.slice(0, 10));
  if (!isValid(start) || !isValid(end)) return 0;
  return Math.max(1, Math.round((differenceInDays(end, start) + 1) / 30.44));
};

const formatTenureLabel = (months: number, style: "card" | "history") => {
  if (!Number.isFinite(months) || months <= 0) return "—";
  if (style === "card" && months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? "1 Year" : `${years} Years`;
  }
  return months === 1 ? "1 month" : `${months} months`;
};

type InsuranceDisplayStatus = "Pending" | "Active" | "Expired";

const isInsuranceExpired = (policy: ScheduleInsurancePolicy, today: Date) => {
  const end = parseISO(policy.end_date.slice(0, 10));
  return isValid(end) && differenceInDays(today, end) > 0;
};

const deriveInsuranceStatus = (policy: ScheduleInsurancePolicy, today: Date): InsuranceDisplayStatus => {
  if (isInsuranceExpired(policy, today)) return "Expired";
  const start = parseISO(policy.start_date.slice(0, 10));
  if (isValid(start) && differenceInDays(start, today) > 0) return "Pending";
  return "Active";
};

const splitScheduleInsurance = (policies: ScheduleInsurancePolicy[], today: Date) => {
  const history = policies
    .filter((policy) => isInsuranceExpired(policy, today))
    .sort((a, b) => b.end_date.localeCompare(a.end_date));
  const current = policies.filter((policy) => !isInsuranceExpired(policy, today));
  const inForce = current.filter((policy) => {
    const start = parseISO(policy.start_date.slice(0, 10));
    return isValid(start) && differenceInDays(today, start) >= 0;
  });
  const pool = inForce.length > 0 ? inForce : current;
  const active = pool.slice().sort((a, b) => b.end_date.localeCompare(a.end_date))[0] ?? null;
  return { active, history };
};

const collectAssetIdsFromShipmentRow = (row: Record<string, unknown>): string[] => {
  const ids = new Set<string>();
  const rawIds = [
    row.individual_asset_id,
    row.individualAssetId,
    row.asset_id,
    row.assetId,
  ];
  for (const raw of rawIds) {
    if (raw == null) continue;
    if (Array.isArray(raw)) {
      raw.forEach((id) => {
        const text = String(id).trim();
        if (text) ids.add(text);
      });
    } else {
      const text = String(raw).trim();
      if (text) ids.add(text);
    }
  }
  const nestedAssets = row.assets ?? row.asset_details ?? row.assetDetails;
  if (Array.isArray(nestedAssets)) {
    nestedAssets.forEach((asset) => {
      if (!asset || typeof asset !== "object") return;
      collectAssetIdsFromShipmentRow(asset as Record<string, unknown>).forEach((id) => ids.add(id));
    });
  }
  return [...ids];
};

const buildAssetShipmentIdMap = (
  shipmentAssetDetails: Array<Record<string, unknown>> | undefined,
  shipmentDetails: Array<Record<string, unknown>> | undefined,
): Map<string, string> => {
  const map = new Map<string, string>();
  const shipments = shipmentDetails ?? [];
  const fallbackShipmentId = shipments.length === 1
    ? String(shipments[0]?.shipment_id ?? shipments[0]?.shipmentId ?? "").trim() || null
    : null;

  for (const row of shipmentAssetDetails ?? []) {
    const shipmentId = String(row.shipment_id ?? row.shipmentId ?? fallbackShipmentId ?? "").trim();
    if (!shipmentId) continue;
    for (const assetId of collectAssetIdsFromShipmentRow(row)) {
      if (!map.has(assetId)) map.set(assetId, shipmentId);
    }
  }
  return map;
};

const findShipmentIdForAsset = (
  asset: Asset,
  assetShipmentMap: Map<string, string>,
  shipments: Array<Record<string, unknown>> | undefined,
): string | null => {
  const candidateIds = getAssetMatchIds(asset);
  for (const id of candidateIds) {
    const shipmentId = assetShipmentMap.get(id);
    if (shipmentId) return shipmentId;
  }

  for (const shipment of shipments ?? []) {
    const shipmentId = shipment.shipment_id ?? shipment.shipmentId ?? shipment.id;
    if (shipmentId == null || !String(shipmentId).trim()) continue;
    const assetIds = [
      ...collectAssetIdsFromShipmentRow(shipment),
      ...(Array.isArray(shipment.assetIds) ? shipment.assetIds : []),
      ...(Array.isArray(shipment.asset_ids) ? shipment.asset_ids : []),
    ].map((id) => String(id).trim()).filter(Boolean);
    if (assetIds.some((id) => candidateIds.has(id))) {
      return String(shipmentId).trim();
    }
  }

  return null;
};

const assetMatchesRecord = (asset: Asset, assetRecordId: string) => {
  const row = asset as Asset & Record<string, unknown>;
  return asset.assetId === assetRecordId
    || String(row.id ?? "").trim() === assetRecordId
    || String(row.individual_asset_id ?? "").trim() === assetRecordId;
};

const normalizeApprovedNoaAsset = (asset: Record<string, unknown>) => ({
  assetId: String(asset.assetId ?? asset.asset_id ?? asset.individual_asset_id ?? asset.id ?? "").trim(),
  id: asset.id != null ? String(asset.id) : undefined,
  individual_asset_id: asset.individual_asset_id != null ? String(asset.individual_asset_id) : undefined,
  description: String(asset.description ?? asset.assetDescription ?? asset.asset_description ?? ""),
  serialNo: asset.serialNo != null
    ? String(asset.serialNo)
    : asset.serial_no != null
      ? String(asset.serial_no)
      : undefined,
  basePrice: toScheduleNumber(asset.basePrice ?? asset.base_price ?? asset.price),
  hsn: String(asset.hsn ?? ""),
  gstRate: toScheduleNumber(asset.gstRate ?? asset.gst_rate, 18),
  poNumber: asset.poNumber != null
    ? String(asset.poNumber)
    : asset.po_number != null
      ? String(asset.po_number)
      : undefined,
  poId: asset.poId != null
    ? String(asset.poId)
    : asset.po_id != null
      ? String(asset.po_id)
      : undefined,
  quantity: toScheduleNumber(asset.quantity, 1),
  vendorName: asset.vendorName != null
    ? String(asset.vendorName)
    : asset.vendor_name != null
      ? String(asset.vendor_name)
      : undefined,
  supplierId: asset.supplierId != null
    ? String(asset.supplierId)
    : asset.supplier_id != null
      ? String(asset.supplier_id)
      : undefined,
  deliveryAddress: asset.deliveryAddress != null
    ? String(asset.deliveryAddress)
    : asset.delivery_address != null
      ? String(asset.delivery_address)
      : undefined,
  productType: asset.productType != null ? String(asset.productType) : undefined,
  requirementId: asset.requirementId != null ? String(asset.requirementId) : undefined,
  specification: asset.specification != null ? String(asset.specification) : undefined,
  serialNumber: asset.serialNumber != null
    ? String(asset.serialNumber)
    : asset.serial_number != null
      ? String(asset.serial_number)
      : undefined,
  category: asset.category != null
    ? String(asset.category)
    : asset.assetCategory != null
      ? String(asset.assetCategory)
      : asset.asset_category != null
        ? String(asset.asset_category)
        : undefined,
  makeBrand: asset.makeBrand != null
    ? String(asset.makeBrand)
    : asset.make_brand != null
      ? String(asset.make_brand)
      : undefined,
  modelNo: asset.modelNo != null
    ? String(asset.modelNo)
    : asset.model_no != null
      ? String(asset.model_no)
      : undefined,
  condition: asset.condition != null
    ? String(asset.condition)
    : asset.assetType != null
      ? String(asset.assetType)
      : asset.asset_type != null
        ? String(asset.asset_type)
        : undefined,
});

const extractApprovedNoaAssetsSchedule = (response: any, rsId: string): Schedule | null => {
  const payload = response?.data ?? response;
  const raw =
    payload?.rentalSchedule ??
    payload?.schedule ??
    extractPostDisbursementPayload(response) ??
    payload;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const assetsRaw =
    raw.assets ??
    raw.asset ??
    payload?.assets ??
    payload?.asset ??
    (Array.isArray(payload) ? payload : undefined);

  if (!Array.isArray(assetsRaw) || assetsRaw.length === 0) return null;

  const assets = assetsRaw
    .filter((asset: unknown): asset is Record<string, unknown> => !!asset && typeof asset === "object")
    .map((asset) => ({
      ...asset,
      ...normalizeApprovedNoaAsset(asset),
    }))
    .filter(asset => asset.assetId);

  if (assets.length === 0) return null;

  const configRaw = raw.config ?? payload?.config ?? {};
  const emptyUpfront = {
    securityDepositAmountRaw: 0,
    securityDepositAmountRounded: 0,
    processingFeeRaw: 0,
    processingFeeRounded: 0,
    feeGSTRaw: 0,
    feeGSTRounded: 0,
    totalRaw: 0,
    totalRounded: 0,
  };

  const payloadExtras =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? Object.fromEntries(
        Object.entries(payload).filter(([key]) =>
          !["rentalSchedule", "schedule", "assets", "asset", "config", "data"].includes(key),
        ),
      )
      : {};

  return {
    ...payloadExtras,
    ...raw,
    id: String(raw.id ?? raw.scheduleId ?? raw.schedule_id ?? rsId),
    displayId: String(raw.displayId ?? raw.display_id ?? rsId),
    customerID: String(raw.customerID ?? raw.customer_id ?? raw.customerId ?? ""),
    status: String(raw.status ?? "Approved"),
    lines: Array.isArray(raw.lines) ? raw.lines : [],
    upfront: raw.upfront ?? emptyUpfront,
    config: {
      ...configRaw,
      customer: configRaw.customer ?? { customerId: "", name: "" },
      frequency: configRaw.frequency ?? "Monthly",
      interestRatePercent: toScheduleNumber(configRaw.interestRatePercent ?? configRaw.interest_rate_percent),
      tenureMonths: toScheduleNumber(configRaw.tenureMonths ?? configRaw.tenure_months, 12),
      commencementDate: String(configRaw.commencementDate ?? configRaw.commencement_date ?? new Date().toISOString()),
      paymentCycleDay: toScheduleNumber(configRaw.paymentCycleDay ?? configRaw.payment_cycle_day, 1),
      securityDepositPercent: toScheduleNumber(configRaw.securityDepositPercent ?? configRaw.security_deposit_percent),
      processingFeePercent: toScheduleNumber(configRaw.processingFeePercent ?? configRaw.processing_fee_percent),
      fundingType: configRaw.fundingType ?? configRaw.funding_type ?? "postGstDiscounting",
      gstRate: toScheduleNumber(configRaw.gstRate ?? configRaw.gst_rate, 18),
    },
    assets,
    createdAt: String(raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? new Date().toISOString()),
    lenderName: raw.lenderName != null
      ? String(raw.lenderName)
      : raw.lender_name != null
        ? String(raw.lender_name)
        : undefined,
  } as Schedule;
};



export default function ViewPostDisbursementCase() {
  const { rsId = "" } = useParams();

  const navigate = useNavigate();

  const [noa, setNoa] = useState<any | null>(null);
  const [mcaDetails, setMcaDetails] = useState<any | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [approvedNoaAssetsSchedule, setApprovedNoaAssetsSchedule] = useState<Schedule | null>(null);
  const [assetCategories, setAssetCategories] = useState<AssetCategoryOption[]>([]);
  const [shipmentAssetDetails, setShipmentAssetDetails] = useState<Record<string, unknown>[]>([]);
  const [shipmentDetailsList, setShipmentDetailsList] = useState<Record<string, unknown>[]>([]);
  const [compliancePercent, setCompliancePercent] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [downloadingAssetInsuranceExcel, setDownloadingAssetInsuranceExcel] = useState(false);
  const [overviewData, setOverviewData] = useState<OverviewApiPayload | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [schedulePolicies, setSchedulePolicies] = useState<ScheduleInsurancePolicy[]>([]);
  const [scheduleInsuranceLoading, setScheduleInsuranceLoading] = useState(false);
  const isInitialLoadRef = useRef(true);

  const assetShipmentIdMap = useMemo(
    () => buildAssetShipmentIdMap(shipmentAssetDetails, shipmentDetailsList),
    [shipmentAssetDetails, shipmentDetailsList],
  );

  const loadCompliancePercent = useCallback(async () => {
    if (!rsId) {
      setCompliancePercent(0);
      return;
    }
    try {
      const complianceResponse = await apiService.get<unknown>(
        `/disbursement/get-noa-compliance-data-points/${encodeURIComponent(rsId)}`,
      );
      setCompliancePercent(extractCompliancePercent(complianceResponse));
    } catch (error) {
      console.error("Failed to load compliance data:", error);
      setCompliancePercent(0);
    }
  }, [rsId]);

  const loadPostDisbursementData = useCallback(async () => {
    if (!rsId) {
      setNoa(null);
      setMcaDetails(null);
      setSchedule(null);
      setApprovedNoaAssetsSchedule(null);
      setAssetCategories([]);
      setShipmentAssetDetails([]);
      setShipmentDetailsList([]);
      setCompliancePercent(0);
      setOverviewData(null);
      setIsPageLoading(false);
      isInitialLoadRef.current = false;
      return;
    }

    if (isInitialLoadRef.current) {
      setIsPageLoading(true);
    }

    try {
      const [noaResponse, mcaResponse, assetsResponse, categoriesResponse] = await Promise.all([
        apiService.get<any>(`/disbursement/get-approved-noa-details/${encodeURIComponent(rsId)}`),
        apiService.get<any>(`/disbursement/get-approved-noa-mca-details/${encodeURIComponent(rsId)}`),
        apiService.get<any>(`/disbursement/get-approved-noa-assets-details/${encodeURIComponent(rsId)}`),
        apiService.get<any>(`/disbursement/get-asset-categories`),
      ]);

      await loadCompliancePercent();

      const noaPayload = extractPostDisbursementPayload(noaResponse);
      const mcaPayload = extractPostDisbursementPayload(mcaResponse);

      setNoa(noaPayload);
      setMcaDetails(mcaPayload);
      setApprovedNoaAssetsSchedule(extractApprovedNoaAssetsSchedule(assetsResponse, rsId));
      setAssetCategories(extractAssetCategories(categoriesResponse));

      const noaIdForShipments = mcaPayload?.noa_id ?? noaPayload?.noaId ?? noaPayload?.noa_id;
      if (noaIdForShipments) {
        try {
          const shipmentRes = await apiService.get<any>(
            `/disbursement/get-invoice-assets/${encodeURIComponent(String(noaIdForShipments))}/${encodeURIComponent(rsId)}`,
          );
          setShipmentAssetDetails(shipmentRes?.data?.asset_details ?? shipmentRes?.asset_details ?? []);
          setShipmentDetailsList(shipmentRes?.data?.shipment_details ?? shipmentRes?.shipment_details ?? []);
        } catch (error) {
          console.error("Failed to load shipment asset details:", error);
          setShipmentAssetDetails([]);
          setShipmentDetailsList([]);
        }

        try {
          setOverviewLoading(true);
          const overviewResponse = await apiService.post<unknown>("/disbursement/get-overview-data", {
            rsId,
            noaId: noaIdForShipments,
          });
          setOverviewData(extractOverviewPayload(overviewResponse));
        } catch (error) {
          console.error("Failed to load overview data:", error);
          setOverviewData(null);
        } finally {
          setOverviewLoading(false);
        }
      } else {
        setShipmentAssetDetails([]);
        setShipmentDetailsList([]);
        setOverviewData(null);
      }

      const scheduleId = extractScheduleId(noaPayload) || extractScheduleId(mcaPayload);
      if (!scheduleId) {
        setSchedule(null);
        return;
      }

      try {
        const scheduleResponse = await apiService.get<any>(
          `/rental-schedule/rental-schedules/${encodeURIComponent(scheduleId)}`,
        );

        setSchedule(extractRentalScheduleFromResponse(scheduleResponse));
      } catch (error) {
        console.error("Failed to load rental schedule:", error);
        setSchedule(null);
      }
    } finally {
      if (isInitialLoadRef.current) {
        setIsPageLoading(false);
        isInitialLoadRef.current = false;
      }
    }
  }, [rsId, loadCompliancePercent]);

  const refreshAfterComplianceAction = useCallback(async () => {
    await loadPostDisbursementData();
    await loadCompliancePercent();
  }, [loadPostDisbursementData, loadCompliancePercent]);

  const handleExportAssetInsuranceExcel = useCallback(async () => {
    if (!rsId || downloadingAssetInsuranceExcel) return;
    setDownloadingAssetInsuranceExcel(true);
    try {
      const blob = await apiService.get<Blob>(
        `/disbursement/download-asset-insurance-excel/${encodeURIComponent(rsId)}`,
        { responseType: "blob" },
      );

      const fileName = `${rsId}-Asset-Insurance-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (error: any) {
      console.error("Failed to export asset insurance Excel:", error);
      toast.error(error?.message || "Failed to export Excel");
    } finally {
      setDownloadingAssetInsuranceExcel(false);
    }
  }, [rsId, downloadingAssetInsuranceExcel]);

  const loadScheduleInsurance = useCallback(async (options?: { silent?: boolean }) => {
    if (!rsId) return;
    if (!options?.silent) setScheduleInsuranceLoading(true);
    try {
      const response = await apiService.get<unknown>(
        `/disbursement/get-asset-insurance-by-rental-schedule/${encodeURIComponent(rsId)}`,
      );
      setSchedulePolicies(normalizeScheduleInsurancePolicies(response));
    } catch (error: any) {
      console.error("Failed to load rental schedule insurance:", error);
      toast.error(error?.message || "Failed to load insurance");
      setSchedulePolicies([]);
    } finally {
      if (!options?.silent) setScheduleInsuranceLoading(false);
    }
  }, [rsId]);

  const uploadScheduleInsuranceDocument = useCallback(async (
    insuranceId: string,
    kind: "policy" | "quotation",
    file: File,
  ) => {
    const extension = file.name.includes(".")
      ? `.${file.name.split(".").pop()?.toLowerCase()}`
      : "";
    const allowed = kind === "policy" ? [".pdf"] : [".pdf", ".xlsx"];
    if (!allowed.includes(extension)) {
      toast.error(kind === "policy"
        ? "Policy must be a PDF file."
        : "Quotation must be a PDF or XLSX file.");
      return;
    }

    const formData = new FormData();
    formData.append("insurance_id", insuranceId);
    if (kind === "policy") {
      formData.append("policy_file", file);
      formData.append("policy_filename", file.name);
    } else {
      formData.append("quotation_file", file);
      formData.append("quotation_filename", file.name);
    }

    try {
      await apiService.post("/disbursement/update-rental-schedule-asset-insurance", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(kind === "policy" ? "Policy uploaded." : "Quotation uploaded.");
      await loadScheduleInsurance({ silent: true });
    } catch (error: any) {
      console.error("Failed to upload insurance document:", error);
      toast.error(error?.message || error?.error || "Failed to upload document");
      throw error;
    }
  }, [loadScheduleInsurance]);

  const createScheduleInsurance = useCallback(async (input: {
    customerId: string;
    provider: string;
    premium: string;
    startDate: string;
    endDate: string;
    tenureMonths: string;
    remarks?: string;
    coordinatorName?: string;
    coordinatorEmail?: string;
    coordinatorPhone?: string;
    policyFile?: File | null;
    quotationFile?: File | null;
  }) => {
    const formData = new FormData();
    formData.append("rental_schedule_id", rsId);
    formData.append("customer_id", input.customerId);
    formData.append("start_date", input.startDate);
    formData.append("end_date", input.endDate);
    formData.append("tenure_months", input.tenureMonths);
    formData.append("insurance_provider", input.provider);
    formData.append("premium", input.premium);
    if (input.remarks) formData.append("remarks", input.remarks);
    if (input.coordinatorName) formData.append("coordinator_name", input.coordinatorName);
    if (input.coordinatorEmail) formData.append("coordinator_email", input.coordinatorEmail);
    if (input.coordinatorPhone) formData.append("coordinator_phone", input.coordinatorPhone);
    if (input.policyFile) formData.append("policy_file", input.policyFile);
    if (input.quotationFile) formData.append("quotation_file", input.quotationFile);

    try {
      await apiService.post("/disbursement/create-rental-schedule-asset-insurance", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Policy added.");
      await loadScheduleInsurance({ silent: true });
      await loadCompliancePercent();
    } catch (error: any) {
      console.error("Failed to add insurance policy:", error);
      toast.error(error?.message || error?.error || "Failed to add insurance policy");
      throw error;
    }
  }, [rsId, loadScheduleInsurance, loadCompliancePercent]);

  const updateScheduleInsurance = useCallback(async (input: {
    insuranceId: string;
    customerId: string;
    provider: string;
    premium: string;
    startDate: string;
    endDate: string;
    tenureMonths: string;
    remarks?: string;
    coordinatorName?: string;
    coordinatorEmail?: string;
    coordinatorPhone?: string;
    policyFile?: File | null;
    quotationFile?: File | null;
  }) => {
    const formData = new FormData();
    formData.append("insurance_id", input.insuranceId);
    formData.append("customer_id", input.customerId);
    formData.append("start_date", input.startDate);
    formData.append("end_date", input.endDate);
    formData.append("tenure_months", input.tenureMonths);
    formData.append("insurance_provider", input.provider);
    formData.append("premium", input.premium);
    if (input.remarks) formData.append("remarks", input.remarks);
    if (input.coordinatorName) formData.append("coordinator_name", input.coordinatorName);
    if (input.coordinatorEmail) formData.append("coordinator_email", input.coordinatorEmail);
    if (input.coordinatorPhone) formData.append("coordinator_phone", input.coordinatorPhone);
    if (input.policyFile) formData.append("policy_file", input.policyFile);
    if (input.quotationFile) formData.append("quotation_file", input.quotationFile);

    try {
      await apiService.post("/disbursement/update-rental-schedule-asset-insurance", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Policy updated.");
      await loadScheduleInsurance({ silent: true });
      await loadCompliancePercent();
    } catch (error: any) {
      console.error("Failed to update insurance policy:", error);
      toast.error(error?.message || error?.error || "Failed to update insurance policy");
      throw error;
    }
  }, [loadScheduleInsurance, loadCompliancePercent]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    setIsPageLoading(true);
    setDetailTab("overview");
    setSchedulePolicies([]);
  }, [rsId]);

  useEffect(() => {
    void loadPostDisbursementData();
  }, [loadPostDisbursementData]);

  useEffect(() => {
    void loadScheduleInsurance({ silent: true });
  }, [loadScheduleInsurance]);

  const [undoMcaSubmitting, setUndoMcaSubmitting] = useState(false);

  const handleUndoMcaNotApplicable = useCallback(async () => {
    const noaId = mcaDetails?.noa_id;
    if (noaId == null || noaId === "") {
      toast.error("MCA details are missing. Please refresh and try again.");
      return;
    }

    setUndoMcaSubmitting(true);
    try {
      await apiService.post("/disbursement/update-mca-not-applicable-reason", {
        reason: null,
        noa_id: noaId,
      });
      toast.success("MCA charge filing reset to pending.");
      await refreshAfterComplianceAction();
    } catch (error: any) {
      console.error("Failed to undo MCA not applicable:", error);
      toast.error(error?.message || "Failed to undo MCA not applicable status");
    } finally {
      setUndoMcaSubmitting(false);
    }
  }, [mcaDetails, refreshAfterComplianceAction]);

  const c = noa;
  const shipmentSchedule = approvedNoaAssetsSchedule;
  const hasActiveInsurance = useMemo(
    () => !!splitScheduleInsurance(schedulePolicies, new Date()).active,
    [schedulePolicies],
  );
  const overviewChecklist = useMemo(
    () => buildOverviewChecklist(
      overviewData,
      noa?.disbursementDate ?? noa?.disbursement_date ?? null,
      hasActiveInsurance,
    ),
    [overviewData, noa?.disbursementDate, noa?.disbursement_date, hasActiveInsurance],
  );
  // const noa = noAs.find((n:any) => n.noaId === c?.noaId);
  const assetMap = useMemo(() => {
    const m = new Map<string, string>();
    schedule?.assets.forEach(a => m.set(a.assetId, a.description));
    return m;
  }, [schedule]);

  const [shipmentPiRef, setShipmentPiRef] = useState<string | null>(null);
  // const shipmentPi = c?.supplierInvoices.find((si:any) => si.ref === shipmentPiRef) || null;
  const [nachModalOpen, setNachModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [selectedPo, setSelectedPo] = useState<any>(null);
  const [drawerSupplier, setDrawerSupplier] = useState<any>(null);
  const [drawerCustomer, setDrawerCustomer] = useState<any>(null);
  const [drawerInvoices, setDrawerInvoices] = useState<any[]>([]);

  const assignedRentals = useMemo(() => {
    if (!schedule || !noa) return [];
    return schedule.lines
      .filter((line: any) => noa.selectedRentals.includes(line.scheduleLineId))
      .map((line: any) => {
        const totalGST = (line.gst12Rounded || 0) + (line.gst18Rounded || 0) + (line.gst28Rounded || 0);
        return {
          periodNo: typeof line.periodNo === "number" ? line.periodNo : (line.periodNo === "i" ? "i" : parseInt(String(line.periodNo)) || 0),
          fromDate: line.fromDate,
          toDate: line.toDate,
          dueDate: line.dueDate,
          baseRent: line.baseRentRounded,
          gst: totalGST,
          tds: line.tdsRounded || 0,
          total: line.totalRounded,
        };
      });
  }, [schedule, noa]);

  const firstRentalDate = useMemo(() => {
    if (assignedRentals.length > 0) return assignedRentals[0].dueDate;
    return schedule?.lines?.[0]?.dueDate;
  }, [assignedRentals, schedule]);

  const getPOById = async (poId: string) => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-purchase-order-by-invoiced-id/${poId}`,
      );
      return response?.data ?? null;
    } catch (error) {
      console.error("Error fetching PO for asset:", error);
      return null;
    }
  };

  const toSidebarAsset = (
    asset: Asset & Record<string, unknown>,
    context: {
      customerId?: string | null;
      noaId?: string | number | null;
      shipmentId?: string | null;
    },
  ) => {
    const recordId = String(asset.id ?? asset.assetId ?? "").trim();
    const gst = Number(asset.gstRate ?? 18) || 18;
    return {
      ...asset,
      id: recordId,
      item: asset.item || asset.description || "",
      description: asset.description || asset.item || "",
      unit_price: asset.basePrice ?? (asset as any).unit_price ?? 0,
      qty: asset.quantity ?? 1,
      igst: gst,
      cgst: 0,
      sgst: 0,
      utgst: 0,
      serial_number:
        asset.serialNumber
        ?? (asset as any).serial_number
        ?? asset.serialNo
        ?? "",
      asset_category: (asset as any).category ?? (asset as any).asset_category ?? "",
      make_brand: (asset as any).makeBrand ?? (asset as any).make_brand ?? "",
      model_no: (asset as any).modelNo ?? (asset as any).model_no ?? "",
      asset_type: (asset as any).condition ?? (asset as any).asset_type ?? "",
      specification: (asset as any).specification ?? "",
      po_id: asset.poId ?? (asset as any).po_id,
      invoice_id: asset.invoiceId ?? (asset as any).invoice_id,
      delivery_address: asset.deliveryAddress ?? (asset as any).delivery_address,
      purchase_order_code: asset.poNumber ?? (asset as any).po_number,
      customer_id: context.customerId ?? (asset as any).customer_id,
      noa_id: context.noaId ?? (asset as any).noa_id ?? (asset as any).noaId,
      shipment_id: context.shipmentId ?? (asset as any).shipment_id ?? null,
      primaryImageUrl: (asset as any).primaryImageUrl ?? null,
    };
  };

  const handleAssetViewDetails = async (asset: Asset) => {
    const assetRow = asset as Asset & Record<string, unknown>;
    const noaId = mcaDetails?.noa_id ?? noa?.noaId ?? noa?.noa_id ?? null;
    const customerId = noa?.customer_id != null ? String(noa.customer_id) : null;
    const shipmentId = findShipmentIdForAsset(
      asset,
      assetShipmentIdMap,
      [...(noa?.shipments ?? []), ...shipmentDetailsList],
    );

    const sidebarAsset = toSidebarAsset(assetRow, {
      customerId,
      noaId,
      shipmentId,
    });

    const poId = String(asset.poId ?? assetRow.po_id ?? "").trim();
    const fallbackPo = {
      id: poId || undefined,
      purchaseOrderCode: asset.poNumber || (poId ? `PO-${poId}` : "—"),
      supplierId: asset.supplierId,
      customerId: customerId ?? undefined,
      noa_id: noaId,
      noaId,
    };

    setSelectedAsset(sidebarAsset);
    setSelectedPo(fallbackPo);
    setDrawerSupplier(
      asset.vendorName || asset.supplierId
        ? { companyName: asset.vendorName, id: asset.supplierId }
        : null,
    );
    setDrawerCustomer(
      customerId || noa?.entityName
        ? { customerId, entityName: noa?.entityName }
        : null,
    );
    setDrawerInvoices([]);
    setAssetOpen(false);
    requestAnimationFrame(() => setAssetOpen(true));

    if (!poId) return;

    const poData = await getPOById(poId);
    const resolvedPo = poData
      ? {
          ...poData,
          id: poData.id ?? poId,
          customerId: poData.customerId ?? poData.customer_id ?? customerId,
          supplierId: poData.supplierId ?? poData.supplier_id ?? asset.supplierId,
          purchaseOrderCode:
            poData.purchaseOrderCode
            ?? poData.purchase_order_code
            ?? poData.po_number
            ?? asset.poNumber
            ?? `PO-${poId}`,
          noa_id: poData.noa_id ?? poData.noaId ?? noaId,
          noaId: poData.noaId ?? poData.noa_id ?? noaId,
        }
      : fallbackPo;

    setSelectedPo(resolvedPo);

    const resolvedSupplierId = String(resolvedPo.supplierId ?? asset.supplierId ?? "").trim();
    const resolvedCustomerId = resolvedPo.customerId
      ? String(resolvedPo.customerId)
      : String(customerId ?? "");
    const resolvedPoId = String(resolvedPo.id ?? poId);

    try {
      if (resolvedPoId) {
        const invoicesRes = await apiService.get<any>(
          `/purchase-order/get-invoices/${resolvedPoId}`,
        );
        const list = extractInvoicesList(invoicesRes);
        setDrawerInvoices(list);

        const existingInvoiceId = sidebarAsset.invoice_id ?? sidebarAsset.invoiceId;
        if (!existingInvoiceId && list.length) {
          const lineIds = [
            String(sidebarAsset.id ?? ""),
            asset.assetId ? String(asset.assetId) : "",
          ].filter(Boolean);
          const matched =
            list.find((inv) => {
              const lines = inv?.assetLineIds ?? inv?.asset_line_ids ?? [];
              return Array.isArray(lines)
                && lines.some((id: unknown) => lineIds.includes(String(id)));
            })
            ?? (list.length === 1 ? list[0] : null);

          if (matched?.id != null) {
            setSelectedAsset((prev: any) =>
              prev
                ? {
                    ...prev,
                    invoice_id: matched.id,
                    invoiceId: matched.id,
                    invoice_number:
                      matched.number
                      ?? matched.invoice_number
                      ?? matched.invoiceNumber
                      ?? prev.invoice_number,
                  }
                : prev,
            );
          }
        }
      }
    } catch (error) {
      console.error("Error loading invoices for asset drawer:", error);
    }

    try {
      const [supplierRes, customerRes] = await Promise.all([
        resolvedSupplierId
          ? apiService.get<any>(`/supplier/get-supplier-by-id/${resolvedSupplierId}`)
          : Promise.resolve(null),
        resolvedCustomerId
          ? apiService.get<any>(`/purchase-order/customer-name/${resolvedCustomerId}`)
          : Promise.resolve(null),
      ]);

      if (supplierRes?.data ?? supplierRes?.supplier) {
        setDrawerSupplier(supplierRes?.data ?? supplierRes?.supplier);
      }
      if (customerRes?.data) {
        setDrawerCustomer(customerRes.data);
      }
    } catch (error) {
      console.error("Error loading asset drawer parties:", error);
    }
  };

  const handleUpdateAssetDetails = useCallback((updated: any) => {
    const assetRecordId = String(updated?.id ?? "").trim();
    setSelectedAsset((prev: any) =>
      prev && String(prev.id) === assetRecordId ? { ...prev, ...updated } : prev,
    );
    setApprovedNoaAssetsSchedule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        assets: prev.assets.map((item) => {
          const matches =
            assetMatchesRecord(item, assetRecordId)
            || (updated.assetId && item.assetId === updated.assetId);
          if (!matches) return item;
          return {
            ...item,
            ...updated,
            specification: updated.specification ?? item.specification,
            serialNumber:
              updated.serial_number
              ?? updated.serialNumber
              ?? item.serialNumber,
            category:
              updated.asset_category
              ?? updated.category
              ?? item.category,
            makeBrand:
              updated.make_brand
              ?? updated.makeBrand
              ?? item.makeBrand,
            modelNo:
              updated.model_no
              ?? updated.modelNo
              ?? item.modelNo,
            condition:
              updated.asset_type
              ?? updated.condition
              ?? item.condition,
          };
        }),
      };
    });
  }, []);

  const handlePrimaryImageChange = useCallback((assetId: string, imageUrl: string) => {
    setSelectedAsset((prev: any) =>
      prev && String(prev.id) === String(assetId)
        ? { ...prev, primaryImageUrl: imageUrl }
        : prev,
    );
    setApprovedNoaAssetsSchedule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        assets: prev.assets.map((item) =>
          assetMatchesRecord(item, String(assetId))
            ? { ...item, primaryImageUrl: imageUrl }
            : item,
        ),
      };
    });
  }, []);

  if (isPageLoading) {
    return (
      <div
        className="relative min-h-[60vh] w-full flex flex-col items-center justify-center gap-2 bg-background"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-muted-foreground">Loading case details…</p>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="w-full p-8 bg-white">
        <Button variant="ghost" onClick={() => navigate("/manage-post-disbursement")}><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
        <Card className="mt-4"><CardContent className="p-12 text-center text-muted-foreground">Case not found.</CardContent></Card>
      </div>
    );
  }

  const chargeDueDate = addDays(parseISO(c.disbursementDate), DUE_WINDOW_DAYS);
  const d = daysLeft(format(chargeDueDate, "yyyy-MM-dd"));

  const bucket = daysLeftBucket(d);
  const toneClass = bucket.tone === "destructive" ? "text-destructive" : bucket.tone === "red" ? "text-red-600" : bucket.tone === "amber" ? "text-amber-600" : "text-emerald-600";

  const loadStoredFileBlob = async (fileRef: string) => {
    const response = await apiService.post<Blob>(
      "/data-room/get-file",
      { url: fileRef },
      { responseType: "blob" },
    );
    const blob = response as Blob;
    if (blob.type === "application/json") {
      const errBody = JSON.parse(await blob.text());
      throw new Error(errBody?.message || "Failed to download file");
    }
    if (!blob.size || blob.type.includes("text/html")) {
      throw new Error("Invalid file response");
    }
    return blob;
  };

  const handleDownload = async (fileRef: string, fileName?: string) => {
    if (!fileRef) {
      toast.error("File not available for download");
      return;
    }

    const baseName = fileName || getFileNameFromStoragePath(fileRef, "mca-certificate");

    const triggerBlobDownload = (blob: Blob) => {
      const downloadName = ensureDownloadFileName(baseName, fileRef, blob.type);
      const mimeType = resolveDownloadMimeType(blob, downloadName);
      const fileBlob =
        blob.type === mimeType || mimeType === "application/octet-stream"
          ? blob
          : new Blob([blob], { type: mimeType });
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    };

    if (fileRef.startsWith("blob:") || isAbsoluteFileUrl(fileRef)) {
      const link = document.createElement("a");
      link.href = fileRef;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = ensureDownloadFileName(baseName, fileRef);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    try {
      triggerBlobDownload(await loadStoredFileBlob(fileRef));
    } catch (error: any) {
      console.error("Download error:", error);
      if (isAbsoluteFileUrl(fileRef)) {
        window.open(fileRef, "_blank", "noopener,noreferrer");
        toast.error("Could not download file. Opened in a new tab instead.");
      } else {
        toast.error(error?.message || "Failed to download file");
      }
    }
  };

  const handlePreviewFile = async (fileRef: string, fileName?: string) => {
    if (!fileRef) {
      toast.error("File not available");
      return;
    }
    if (fileRef.startsWith("blob:") || isAbsoluteFileUrl(fileRef)) {
      window.open(fileRef, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const blob = await loadStoredFileBlob(fileRef);
      const downloadName = ensureDownloadFileName(
        fileName || getFileNameFromStoragePath(fileRef, "insurance-document"),
        fileRef,
        blob.type,
      );
      const mimeType = resolveDownloadMimeType(blob, downloadName);
      const fileBlob = blob.type === mimeType || mimeType === "application/octet-stream"
        ? blob
        : new Blob([blob], { type: mimeType });
      const objectUrl = window.URL.createObjectURL(fileBlob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error: any) {
      console.error("Preview error:", error);
      toast.error(error?.message || "Failed to open file");
    }
  };
  function getInitials(text: string): string {
    if (!text || typeof text !== 'string') return '';
    const parts = text.trim().split(/\s+/).filter(Boolean);
    const letters =
      parts.length >= 2
        ? `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`
        : (parts[0]?.slice(0, 2) ?? '');
    return letters.toUpperCase();
  }

  return (
    <div className="w-full min-h-screen p-0 bg-white">
      {/* Header bar */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b">
        <div className="w-full flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/manage-post-disbursement")}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
            <div className="text-xs text-muted-foreground whitespace-nowrap">Post-Disbursement › <span className="text-foreground font-medium">RS NO - {c.displayId}</span></div>
          </div>
        </div>
      </header>

      <main className="w-full min-h-[calc(100vh-56px)] p-1 md:p-4 space-y-6 bg-white">
        {/* Hero — entity identity + KPIs */}
        <div className="rounded-xl border bg-gradient-to-br from-[#8b7355]/5 via-background to-background p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3 flex-nowrap min-w-0 w-full">
                <div className="flex items-center gap-2 min-w-0">
                  {c.customerImage ? (
                    <img
                      src={c.customerImage}
                      alt={c.entityName}
                      className="w-7 h-7 rounded-md flex-shrink-0"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-semibold text-muted-foreground">
                        {getInitials(c.entityName)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex items-center gap-2">
                    {/* <span className="text-[11px] text-muted-foreground uppercase tracking-wide whitespace-nowrap">Customer</span> */}
                    <span className="text-base font-semibold truncate">{c.entityName}</span>
                  </div>
                </div>
                <ArrowLeftRight className="h-4 w-4 text-muted-foreground shrink-0" />
                {c.lenderName ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="rounded-lg shrink-0" style={{ height: "30px", width: "45px" }}>
                      <AvatarImage
                        src={
                          c.lenderAvatar?.startsWith("http")
                            ? c.lenderAvatar
                            : `${import.meta.env.VITE_API_BASE_URL}/${c.lenderAvatar}`
                        }
                        alt={c.lenderName || "Lender"}
                        className="object-contain"
                      />
                    </Avatar>
                    <span className="text-sm text-muted-foreground truncate">{c.lenderName || "—"}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm whitespace-nowrap">{c.lenderName || "—"}</span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat icon={<Hash className="h-3.5 w-3.5" />} label="Rental Schedule No" value={c.displayId || "—"} />
                <Stat
                  icon={<IndianRupee className="h-3.5 w-3.5" />}
                  label="Total Asset Value"
                  value={(Number(c.total_asset_value) || 0).toLocaleString("en-IN")}
                />
                <Stat icon={<Calendar className="h-3.5 w-3.5" />} label="Disbursement Date" value={format(parseISO(c.disbursementDate), "dd MMM yyyy")} />
                <div className="rounded-lg border bg-background/60 p-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><History className="h-3.5 w-3.5" />Days to Deadline</div>
                  <div className="mt-1 flex justify-center">
                    {c.caseStatus === "Complete" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30">
                        <CheckCircle2 className="h-3.5 w-3.5" />Completed
                      </span>
                    ) : d < 0 ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/30">
                        🔴 Overdue by {Math.abs(d)} day{Math.abs(d) !== 1 ? "s" : ""}
                      </span>
                    ) : d <= 7 ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-700 border border-amber-500/30">
                        🟠 {d} day{d !== 1 ? "s" : ""} left
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30">
                        🟢 {d} days left
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Completion ring */}
            <div className="flex flex-col items-center justify-center md:w-48 gap-2">
              <div className="relative h-28 w-28">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                  {/* Always-visible grey track (0% still shows a full grey ring) */}
                  <circle cx="50" cy="50" r="42" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                  {compliancePercent > 0 && (
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      stroke="hsl(43, 75.90%, 39.00%)"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 42}`}
                      strokeDashoffset={`${2 * Math.PI * 42 * (1 - Math.min(100, Math.max(0, compliancePercent)) / 100)}`}
                      strokeLinecap="round"
                    />
                  )}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-2xl font-semibold tabular-nums">{compliancePercent}%</div>
              </div>
              <div className="text-xs text-muted-foreground">Overall completion</div>
            </div>
          </div>
        </div>

        <Tabs
          value={detailTab}
          onValueChange={(value) => {
            setDetailTab(value);
            if (value === "insurance") void loadScheduleInsurance();
          }}
          className="w-full bg-white"
        >
          <TabsList className="mb-3 inline-flex h-9 w-full items-center justify-start gap-1 rounded-lg bg-gray-100 p-1">
            <TabsTrigger
              value="overview"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="statutory-mandate"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Statutory & Mandate
            </TabsTrigger>
            <TabsTrigger
              value="pi-documents"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              PI & Documents
            </TabsTrigger>
            <TabsTrigger
              value="insurance"
              className="cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Insurance
            </TabsTrigger>
            <TabsTrigger
              value="assets"
              className="cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Assets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0 min-h-[calc(100vh-220px)] bg-white">
            <Card>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base leading-tight">Compliance Checklist</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  30-day post-disbursement obligations for this schedule.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-4">
                {overviewLoading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading overview…
                  </div>
                ) : (
                  <div className="divide-y rounded-lg border">
                    {overviewChecklist.map((item) => (
                      <div key={item.key} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            {item.icon}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{item.title}</div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        </div>
                        <OverviewStatusChip status={item.status} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="statutory-mandate" className="mt-0 min-h-[calc(100vh-220px)] bg-white">
        {/* A. Charge Filing + NACH (combined, split) */}
        <Card>
          <CardHeader className="pb-2 pt-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <CardTitle className="text-base leading-tight">Statutory & Mandate Registration</CardTitle>
              <CardDescription className="text-xs truncate">· MCA charge filing + NACH mandate, due in 30 days</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* CHG-1 split */}
              <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-3.5 w-3.5 text-primary shrink-0" />
                    <h3 className="text-sm font-semibold truncate">MCA Charge Filing (CHG-1)</h3>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {(c.mca_s3_file_url === null && mcaDetails.mca_reason === null || mcaDetails.mca_reason === "") && (
                      <>
                        <MarkFiledDialog
                          mcaDetails={mcaDetails}
                          onSuccess={refreshAfterComplianceAction}
                        />
                        <MarkNotApplicableDialog
                          title="Mark Charge Filing as Not Applicable"
                          triggerLabel="N/A"
                          mcaDetails={mcaDetails}
                          onSuccess={refreshAfterComplianceAction}
                        />
                      </>
                    )}
                    {(c.mca_s3_file_url === null && mcaDetails.mca_reason !== null && mcaDetails.mca_reason !== "") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={undoMcaSubmitting}
                        onClick={() => void handleUndoMcaNotApplicable()}
                      >{undoMcaSubmitting ? "Undoing…" : "Undo"}</Button>
                    )}
                    {c.mca_s3_file_url !== null
                      ? <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5"><CheckCircle2 className="h-3 w-3 mr-1" />Filed</Chip>
                      : c.mca_s3_file_url === null
                        ? <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">Pending</Chip>
                        : <Chip className="bg-muted text-muted-foreground text-[10px] px-2 py-0.5">N/A</Chip>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
                  <InlineKV label="Disbursed" value={format(parseISO(c.disbursementDate), "dd MMM yyyy")} />
                  <InlineKV
                    label="Due"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        {format(chargeDueDate, "dd MMM yyyy")}
                        {c.mca_s3_file_url === null && (
                          <span className={`text-[10px] font-medium ${toneClass}`}>· {bucket.label}</span>
                        )}
                      </span>
                    }
                  />
                  {mcaDetails.mca_s3_file_url !== null && (
                    <>
                      <InlineKV label="Filed On" value={mcaDetails.mca_filing_date ? format(parseISO(mcaDetails.mca_filing_date), "dd MMM yyyy") : "—"} />
                      <InlineKV
                        label="Certificate"
                        value={mcaDetails.mca_s3_file_url ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              const fileRef = mcaDetails.mca_doc_url || mcaDetails.mca_s3_file_url;
                              void handleDownload(fileRef, mcaDetails.mca_filename);
                            }}
                            className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                          >
                            <Paperclip className="h-3 w-3" />{mcaDetails.mca_filename}
                          </a>
                        ) : "—"}
                      />
                    </>
                  )}
                  {mcaDetails.mca_s3_file_url === null && (
                    <InlineKV label="Reason" value={mcaDetails.mca_reason || "—"} />
                  )}
                </div>
              </div>

              {/* NACH split */}
              <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CreditCard className="h-3.5 w-3.5 text-primary shrink-0" />
                    <h3 className="text-sm font-semibold truncate">NACH Mandate</h3>
                    <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                      <AvatarImage
                        src={
                          c.lenderAvatar?.startsWith("http")
                            ? c.lenderAvatar
                            : `${import.meta.env.VITE_API_BASE_URL}/${c.lenderAvatar}`
                        }
                        alt={c.lenderName || "Lender"}
                        className="object-contain"
                      />
                    </Avatar>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">

                    {c.noa_nach_active == true
                      ? (
                        <Chip className="bg-emerald-500/10 text-emerald-700 text-[10px] px-2 py-0.5">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Active
                        </Chip>
                      )
                      : (
                        <Chip className="bg-blue-500/10 text-blue-700 text-[10px] px-2 py-0.5">
                          Pending
                        </Chip>
                      )
                    }
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
                  <InlineKV label="Lender" value={c.lenderName || "—"} />
                  <InlineKV label="Funding Mode" value={c.funding_mode ? fundingModeLabel(c.funding_mode) : "—"} />
                  <InlineKV label="1st Rental" value={c.noa_nach_start_date ? format(parseISO(c.noa_nach_start_date), "dd MMM yyyy") : "—"} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="pi-documents" className="mt-0 min-h-[calc(100vh-220px)] bg-white">
        {/* B. Supplier Shipments — user-created entity with TI + EWB uploads */}
        {shipmentSchedule && (
          <SupplierShipmentsSection
            rsId={rsId}
            noaId={String(mcaDetails?.noa_id ?? c?.noaId ?? c?.noa_id ?? "") || undefined}
            customerName={c.entityName}
            customerId={
              String(c.customer_id)
            }
            onShipmentCreated={refreshAfterComplianceAction}
          />
        )}
          </TabsContent>

          <TabsContent value="insurance" className="mt-0 min-h-[calc(100vh-220px)] bg-white">
            {(() => {
              const approvedAssets = approvedNoaAssetsSchedule?.assets ?? [];
              const scheduleSumInsured = approvedAssets.reduce(
                (sum, asset) => sum + Number(asset.total_asset_value ?? 0),
                0,
              ) || Number(c.total_asset_value ?? 0);
              const { active, history } = splitScheduleInsurance(schedulePolicies, new Date());
              return (
                <RentalScheduleInsurancePanel
                  loading={scheduleInsuranceLoading}
                  policies={schedulePolicies}
                  activePolicy={active}
                  historyPolicies={history}
                  scheduleSumInsured={scheduleSumInsured}
                  leaseTenureMonths={Number(c.tenure?.total_months) || 12}
                  customerId={c.customer_id != null ? String(c.customer_id) : ""}
                  onPreviewFile={handlePreviewFile}
                  onDownloadFile={handleDownload}
                  onUploadDocument={uploadScheduleInsuranceDocument}
                  onCreatePolicy={createScheduleInsurance}
                  onUpdatePolicy={updateScheduleInsurance}
                />
              );
            })()}
          </TabsContent>

          <TabsContent value="assets" className="mt-0 min-h-[calc(100vh-220px)] bg-white">
            {(() => {
              const approvedAssets = approvedNoaAssetsSchedule?.assets ?? [];
              return (
                <SectionCard
                  icon={<ShieldAlert className="w-5" />}
                  title="Assets"
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      className="cursor-pointer gap-1"
                      onClick={() => void handleExportAssetInsuranceExcel()}
                      disabled={downloadingAssetInsuranceExcel}
                    >
                      {downloadingAssetInsuranceExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Export
                    </Button>
                  }
                >
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Asset</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead>Serial No</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead className="text-right">Total Value</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvedAssets.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                              No assets found.
                            </TableCell>
                          </TableRow>
                        ) : approvedAssets.map((asset) => (
                          <TableRow key={asset.assetId}>
                            <TableCell>
                              <div className="font-medium text-sm">{asset.item}</div>
                              <div className="text-[11px] text-muted-foreground">{asset.description}</div>
                            </TableCell>
                            <TableCell className="text-sm">{asset.vendorName || <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="text-center">{asset.serialNumber || <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="text-center">{asset.quantity || <span className="text-muted-foreground">—</span>}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{inrFmt(asset?.total_asset_value ?? 0)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 cursor-pointer p-0"
                                onClick={() => void handleAssetViewDetails(asset)}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </SectionCard>
              );
            })()}
          </TabsContent>
        </Tabs>
        
      </main>

      {selectedAsset && (
        <AssetDetailSidebar
          open={assetOpen}
          onOpenChange={setAssetOpen}
          asset={selectedAsset}
          po={selectedPo}
          supplier={drawerSupplier}
          customer={drawerCustomer}
          isIndividualAsset={true}
          assetsCategories={assetCategories}
          invoices={drawerInvoices}
          updateDetails={handleUpdateAssetDetails}
          onPrimaryImageChange={handlePrimaryImageChange}
        />
      )}
    </div>
  );
}


function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-background/60 p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="text-sm font-medium mt-1">{value}</div>
    </div>
  );
}

function MarkFiledDialog({
  mcaDetails,
  onSuccess,
}: {
  mcaDetails: { noa_code?: string; noa_id?: string | number } | null;
  onSuccess?: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [filingDate, setFilingDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const reset = () => { setFilingDate(format(new Date(), "yyyy-MM-dd")); setFile(null); };

  const handleConfirm = async () => {
    if (!filingDate || !file) return;
    const noaCode = mcaDetails?.noa_code;
    const noaId = mcaDetails?.noa_id;
    if (!noaCode || noaId == null || noaId === "") {
      toast.error("MCA details are missing. Please refresh and try again.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("filingDate", filingDate);
      formData.append("noa_code", String(noaCode));
      formData.append("noa_id", String(noaId));
      formData.append("file", file);
      formData.append("filename", file.name);

      await apiService.post("/disbursement/upload-noa-mca-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Charge filing marked as filed.");
      setOpen(false);
      reset();
      await onSuccess?.();
    } catch (error: any) {
      console.error("MCA file upload failed:", error);
      toast.error(error?.message || "Failed to upload MCA filing document");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm">Mark Filed</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark MCA Charge Filing as Filed</DialogTitle>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <div className="min-w-0 space-y-2">
            <Label htmlFor="mca-filing-date">Filing Date</Label>
            <Input
              id="mca-filing-date"
              type="date"
              value={filingDate}
              onChange={(e) => setFilingDate(e.target.value)}
              className="min-w-0 max-w-full"
            />
          </div>
          <div className="min-w-0 space-y-2">
            <Label htmlFor="mca-certificate-file">Certificate of Registration</Label>
            <Input
              id="mca-certificate-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="min-w-0 max-w-full"
            />
            {file && (
              <div className="mt-1 flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="truncate">{file.name}</span>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            disabled={!filingDate || !file || uploading}
            onClick={() => void handleConfirm()}
          >
            {uploading ? "Uploading…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkNotApplicableDialog({
  title,
  triggerLabel,
  mcaDetails,
  onSuccess,
}: {
  title: string;
  triggerLabel: string;
  mcaDetails: { noa_id?: string | number } | null;
  onSuccess?: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) return;
    const noaId = mcaDetails?.noa_id;
    if (noaId == null || noaId === "") {
      toast.error("MCA details are missing. Please refresh and try again.");
      return;
    }

    setSubmitting(true);
    try {
      await apiService.post("/disbursement/update-mca-not-applicable-reason", {
        reason: trimmedReason,
        noa_id: noaId,
      });

      toast.success("Charge filing marked Not Applicable.");
      setOpen(false);
      setReason("");
      await onSuccess?.();
    } catch (error: any) {
      console.error("Failed to update MCA not applicable reason:", error);
      toast.error(error?.message || "Failed to mark charge filing as Not Applicable");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setReason(""); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">{triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-w-0 space-y-2">
          <Label htmlFor="mca-na-reason">Reason (required)</Label>
          <Textarea
            id="mca-na-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Funded via direct debit; lender did not require NACH; charge already filed under earlier MRA…"
            rows={4}
            className="min-w-0 max-w-full"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            disabled={!reason.trim() || submitting}
            onClick={() => void handleConfirm()}
          >
            {submitting ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InlineKV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="inline-flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">{label}</span>
      <span className="text-xs font-medium mt-0.5 leading-tight">{value}</span>
    </div>
  );
}

function InsuranceStatusChip({ status }: { status: InsuranceDisplayStatus }) {
  if (status === "Pending") {
    return <Chip className="bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700">Pending</Chip>;
  }
  if (status === "Active") {
    return <Chip className="bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700">Active</Chip>;
  }
  return <Chip className="bg-red-500/10 px-2 py-0.5 text-xs text-red-700">Expired</Chip>;
}

function InsuranceDetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}

function InsuranceDocumentRow({
  name,
  meta,
  emptyLabel,
  accept,
  busy,
  onPreview,
  onDownload,
  onUpload,
}: {
  name?: string;
  meta?: string;
  emptyLabel: string;
  accept: string;
  busy?: boolean;
  onPreview?: () => void;
  onDownload?: () => void;
  onUpload?: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const extension = (name?.split(".").pop() || "PDF").slice(0, 4).toUpperCase();
  const canUpload = !!onUpload && !name;
  const fileInput = canUpload ? (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="hidden"
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) onUpload?.(file);
      }}
    />
  ) : null;
  const uploadButton = canUpload ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 cursor-pointer"
      disabled={busy}
      onClick={() => inputRef.current?.click()}
      aria-label={emptyLabel}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
    </Button>
  ) : null;

  if (!name) {
    return (
      <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5 text-muted-foreground">
        {fileInput}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-[10px] font-semibold">
          PDF
        </div>
        <span className="min-w-0 flex-1 text-sm">{emptyLabel}</span>
        {uploadButton}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border px-3 py-2">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-red-500/10 text-[10px] font-semibold text-red-600">
        {extension}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium" title={name}>{name}</div>
        {meta ? <div className="text-xs text-muted-foreground">{meta}</div> : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        disabled={busy || !onPreview}
        onClick={onPreview}
        aria-label={`View ${name}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        disabled={busy || !onDownload}
        onClick={onDownload}
        aria-label={`Download ${name}`}
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}

const insuranceFileExtension = (file: File) =>
  file.name.includes(".") ? `.${file.name.split(".").pop()?.toLowerCase()}` : "";

const isValidOptionalEmail = (value: string) =>
  value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getCoordinatorPhoneError = (value: string) => {
  if (!value) return "";
  if (/\s/.test(value)) return "Phone number cannot contain spaces.";
  if (!/^\d+$/.test(value)) return "Phone number can contain digits only.";
  if (value.length > 10) return "Phone number cannot be more than 10 digits.";
  if (value.length < 10) return "Enter a valid 10-digit phone number.";
  if (!/^[6-9]\d{9}$/.test(value)) return "Enter a valid 10-digit mobile number.";
  return "";
};

const isValidOptionalName = (value: string) => {
  const text = value.trim();
  if (!text) return true;
  return text.length >= 2 && /^[A-Za-z][A-Za-z .'-]*$/.test(text);
};

function ScheduleAddPolicyForm({
  maxTenureMonths,
  saving,
  initialPolicy,
  onCancel,
  onSave,
}: {
  maxTenureMonths: number;
  saving: boolean;
  initialPolicy?: ScheduleInsurancePolicy | null;
  onCancel: () => void;
  onSave: (input: {
    provider: string;
    premium: string;
    startDate: string;
    endDate: string;
    tenureMonths: string;
    remarks?: string;
    coordinatorName?: string;
    coordinatorEmail?: string;
    coordinatorPhone?: string;
    policyFile?: File | null;
    quotationFile?: File | null;
  }) => Promise<void>;
}) {
  const isEdit = !!initialPolicy;
  const [startDate, setStartDate] = useState(
    initialPolicy?.start_date?.slice(0, 10) || format(new Date(), "yyyy-MM-dd"),
  );
  const [tenureMonths, setTenureMonths] = useState(() => {
    if (initialPolicy) return String(policyTenureMonths(initialPolicy) || 12);
    return String(Math.min(12, Math.max(1, maxTenureMonths || 12)));
  });
  const [provider, setProvider] = useState(initialPolicy?.insurance_provider ?? "");
  const [premium, setPremium] = useState(
    initialPolicy != null ? String(initialPolicy.premium ?? "") : "",
  );
  const [remarks, setRemarks] = useState(initialPolicy?.remarks ?? "");
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [quotationFile, setQuotationFile] = useState<File | null>(null);
  const [showCoordinator, setShowCoordinator] = useState(
    !!(initialPolicy?.coordinator_name || initialPolicy?.coordinator_email || initialPolicy?.coordinator_phone),
  );
  const [coordinatorName, setCoordinatorName] = useState(initialPolicy?.coordinator_name ?? "");
  const [coordinatorEmail, setCoordinatorEmail] = useState(initialPolicy?.coordinator_email ?? "");
  const [coordinatorPhone, setCoordinatorPhone] = useState(
    (initialPolicy?.coordinator_phone ?? "").replace(/\D/g, "").slice(-10),
  );
  const [error, setError] = useState("");
  const tenureValue = Number(tenureMonths) || 0;
  const tenureTooHigh = maxTenureMonths > 0 && tenureValue > maxTenureMonths;
  const endDate = startDate && tenureValue > 0
    ? format(subDays(addMonths(parseISO(startDate), tenureValue), 1), "yyyy-MM-dd")
    : "";

  const handleSave = async () => {
    if (!startDate) {
      setError("Start date is required.");
      return;
    }
    if (tenureValue < 1) {
      setError("Tenure is required.");
      return;
    }
    if (!endDate) {
      setError("End date is required.");
      return;
    }
    if (tenureTooHigh) {
      setError(`Maximum tenure allowed is ${maxTenureMonths} months.`);
      return;
    }
    if (!provider.trim() || provider.trim().length < 2) {
      setError("Provider is required.");
      return;
    }
    const hasExistingPolicyDoc = !!initialPolicy?.upload_policy_s3_fileurl;
    if (!isEdit && !policyFile) {
      setError("Policy document is required.");
      return;
    }
    if (isEdit && !policyFile && !hasExistingPolicyDoc) {
      setError("Policy document is required.");
      return;
    }
    if (policyFile && insuranceFileExtension(policyFile) !== ".pdf") {
      setError("Policy must be a PDF file.");
      return;
    }
    const premiumValue = premium.trim() === "" ? 0 : Number(premium);
    if (!Number.isFinite(premiumValue) || premiumValue < 0) {
      setError("Enter a valid premium amount.");
      return;
    }
    if (quotationFile && ![".pdf", ".xlsx"].includes(insuranceFileExtension(quotationFile))) {
      setError("Quotation must be a PDF or XLSX file.");
      return;
    }
    if (!isValidOptionalName(coordinatorName)) {
      setError("Coordinator name can use letters, spaces, and . ' - only.");
      return;
    }
    if (!isValidOptionalEmail(coordinatorEmail)) {
      setError("Enter a valid coordinator email.");
      return;
    }
    const phoneError = getCoordinatorPhoneError(coordinatorPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }
    setError("");
    await onSave({
      provider: provider.trim(),
      premium: String(premiumValue),
      startDate,
      endDate,
      tenureMonths: String(tenureValue),
      remarks: remarks.trim() || undefined,
      coordinatorName: coordinatorName.trim() || undefined,
      coordinatorEmail: coordinatorEmail.trim() || undefined,
      coordinatorPhone: coordinatorPhone.trim() || undefined,
      policyFile,
      quotationFile,
    });
  };

  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
      {isEdit ? (
        <div className="text-sm font-semibold">Edit Policy{initialPolicy?.policy_code ? ` · ${initialPolicy.policy_code}` : ""}</div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-start">Start Date <span className="ml-0.5 text-red-600">*</span></Label>
          <Input id="schedule-policy-start" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-tenure">Tenure (months) <span className="ml-0.5 text-red-600">*</span></Label>
          <Input
            id="schedule-policy-tenure"
            inputMode="numeric"
            value={tenureMonths}
            onChange={(event) => setTenureMonths(event.target.value.replace(/\D/g, ""))}
            required
          />
          {tenureTooHigh ? (
            <p className="text-xs text-destructive">Maximum allowed is {maxTenureMonths} months.</p>
          ) : maxTenureMonths > 0 ? (
            <p className="text-xs text-muted-foreground">Max {maxTenureMonths} mo remaining</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-end">End Date (auto) <span className="ml-0.5 text-red-600">*</span></Label>
          <div className="relative">
            <Input
              id="schedule-policy-end"
              type="date"
              value={endDate}
              readOnly
              tabIndex={-1}
              required
              className="pr-9 [&::-webkit-calendar-picker-indicator]:hidden"
            />
            <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-provider">Provider <span className="ml-0.5 text-red-600">*</span></Label>
          <Input id="schedule-policy-provider" value={provider} onChange={(event) => setProvider(event.target.value)} placeholder="e.g. ICICI Lombard" required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-premium">Premium (₹)</Label>
          <Input id="schedule-policy-premium" type="number" min="0" value={premium} onChange={(event) => setPremium(event.target.value)} />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="schedule-policy-remark">Remark</Label>
        <Input
          id="schedule-policy-remark"
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          placeholder="Add a remark"
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="schedule-policy-file">Upload Policy (PDF) <span className="ml-0.5 text-red-600">*</span></Label>
          <Input id="schedule-policy-file" type="file" accept=".pdf,application/pdf" onChange={(event) => setPolicyFile(event.target.files?.[0] ?? null)} required={!isEdit || !initialPolicy?.upload_policy_s3_fileurl} />
          {!policyFile && initialPolicy?.upload_policy_filename ? (
            <p className="text-xs text-muted-foreground">Current: {initialPolicy.upload_policy_filename}</p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="schedule-quotation-file">Upload Quotation (PDF/XLSX)</Label>
          <Input id="schedule-quotation-file" type="file" accept=".pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => setQuotationFile(event.target.files?.[0] ?? null)} />
          {!quotationFile && initialPolicy?.upload_quotation_filename ? (
            <p className="text-xs text-muted-foreground">Current: {initialPolicy.upload_quotation_filename}</p>
          ) : null}
        </div>
      </div>
      {!showCoordinator ? (
        <button type="button" className="cursor-pointer text-xs text-primary" onClick={() => setShowCoordinator(true)}>
          + Add Coordinator Contact (optional)
        </button>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="schedule-coordinator-name">Name</Label>
            <Input id="schedule-coordinator-name" value={coordinatorName} onChange={(event) => setCoordinatorName(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="schedule-coordinator-email">Email</Label>
            <Input id="schedule-coordinator-email" type="email" value={coordinatorEmail} onChange={(event) => setCoordinatorEmail(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="schedule-coordinator-phone">Phone</Label>
            <Input
              id="schedule-coordinator-phone"
              inputMode="numeric"
              maxLength={10}
              value={coordinatorPhone}
              onChange={(event) => {
                const next = event.target.value;
                if (/\s/.test(next)) {
                  setError("Phone number cannot contain spaces.");
                  setCoordinatorPhone(next.replace(/\s/g, "").replace(/\D/g, "").slice(0, 10));
                  return;
                }
                if (next.length > 10) {
                  setError("Phone number cannot be more than 10 digits.");
                  setCoordinatorPhone(next.replace(/\D/g, "").slice(0, 10));
                  return;
                }
                const digitsOnly = next.replace(/\D/g, "");
                if (digitsOnly !== next) {
                  setError("Phone number can contain digits only.");
                  setCoordinatorPhone(digitsOnly.slice(0, 10));
                  return;
                }
                setError("");
                setCoordinatorPhone(digitsOnly);
              }}
              placeholder="10-digit mobile"
            />
            {getCoordinatorPhoneError(coordinatorPhone) ? (
              <p className="text-xs text-destructive">{getCoordinatorPhoneError(coordinatorPhone)}</p>
            ) : null}
          </div>
        </div>
      )}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" className="cursor-pointer" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="button" className="cursor-pointer" onClick={() => void handleSave()} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? "Saving…" : isEdit ? "Update Policy" : "Add Policy"}
        </Button>
      </div>
    </div>
  );
}

function RentalScheduleInsurancePanel({
  loading,
  policies,
  activePolicy,
  historyPolicies,
  scheduleSumInsured,
  leaseTenureMonths,
  customerId,
  onPreviewFile,
  onDownloadFile,
  onUploadDocument,
  onCreatePolicy,
  onUpdatePolicy,
}: {
  loading: boolean;
  policies: ScheduleInsurancePolicy[];
  activePolicy: ScheduleInsurancePolicy | null;
  historyPolicies: ScheduleInsurancePolicy[];
  scheduleSumInsured: number;
  leaseTenureMonths: number;
  customerId: string;
  onPreviewFile: (fileRef: string, fileName?: string) => Promise<void>;
  onDownloadFile: (fileRef: string, fileName?: string) => Promise<void>;
  onUploadDocument: (insuranceId: string, kind: "policy" | "quotation", file: File) => Promise<void>;
  onCreatePolicy: (input: {
    customerId: string;
    provider: string;
    premium: string;
    startDate: string;
    endDate: string;
    tenureMonths: string;
    remarks?: string;
    coordinatorName?: string;
    coordinatorEmail?: string;
    coordinatorPhone?: string;
    policyFile?: File | null;
    quotationFile?: File | null;
  }) => Promise<void>;
  onUpdatePolicy: (input: {
    insuranceId: string;
    customerId: string;
    provider: string;
    premium: string;
    startDate: string;
    endDate: string;
    tenureMonths: string;
    remarks?: string;
    coordinatorName?: string;
    coordinatorEmail?: string;
    coordinatorPhone?: string;
    policyFile?: File | null;
    quotationFile?: File | null;
  }) => Promise<void>;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [policyFormMode, setPolicyFormMode] = useState<"add" | "edit" | null>(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [viewedPolicy, setViewedPolicy] = useState<ScheduleInsurancePolicy | null>(null);
  const today = new Date();
  const coveredMonths = policies
    .filter((item) => !isInsuranceExpired(item, today))
    .reduce((sum, item) => sum + policyTenureMonths(item), 0);
  const remainingMonths = Math.max(0, leaseTenureMonths - coveredMonths);
  const coveragePct = leaseTenureMonths > 0
    ? Math.min(100, Math.round((coveredMonths / leaseTenureMonths) * 100))
    : 0;
  const fullyCovered = leaseTenureMonths > 0 && remainingMonths === 0;
  const policy = activePolicy;
  const activeStatus = policy ? deriveInsuranceStatus(policy, today) : null;
  const endDate = policy ? parseISO(policy.end_date.slice(0, 10)) : null;
  const daysToExpiry = endDate && isValid(endDate) ? differenceInDays(endDate, today) : null;
  const documentDate = policy ? formatPolicyDate(policy.updated_at) : "—";
  const formOpen = policyFormMode != null;
  const editMaxTenureMonths = policy
    ? remainingMonths + policyTenureMonths(policy)
    : remainingMonths;

  const runFileAction = async (key: string, action: () => Promise<void>) => {
    setBusyKey(key);
    try {
      await action();
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">Insurance</h2>
          <p className="text-sm text-muted-foreground">One policy covers all assets of this Rental Schedule.</p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Chip className="bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-700">
              Lease Tenure: {leaseTenureMonths} mo ({Math.max(1, Math.ceil(leaseTenureMonths / 12))}y)
            </Chip>
            <Chip className="bg-muted px-2 py-0.5 text-[10px] text-foreground">
              Covered: {coveredMonths} / {leaseTenureMonths} mo
            </Chip>
            <Chip className={`px-2 py-0.5 text-[10px] ${fullyCovered ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>
              {fullyCovered ? "Fully covered" : `Remaining: ${remainingMonths} mo`}
            </Chip>
          </div>
        </div>
        {!loading && !formOpen && !policy ? (
          <Button type="button" size="sm" className="cursor-pointer gap-1" onClick={() => setPolicyFormMode("add")}>
            <Plus className="h-4 w-4" />
            Add Policy
          </Button>
        ) : null}
        {!loading && !formOpen && policy ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1"
            onClick={() => setPolicyFormMode("edit")}
          >
            <Pencil className="h-4 w-4" />
            Edit Policy
          </Button>
        ) : null}
      </div>
      {!loading ? <Progress value={coveragePct} className="h-2" /> : null}
      {policyFormMode === "add" ? (
        <ScheduleAddPolicyForm
          maxTenureMonths={remainingMonths}
          saving={savingPolicy}
          onCancel={() => setPolicyFormMode(null)}
          onSave={async (input) => {
            if (!customerId) {
              toast.error("Customer ID is required to add an insurance policy.");
              return;
            }
            setSavingPolicy(true);
            try {
              await onCreatePolicy({ customerId, ...input });
              setPolicyFormMode(null);
            } finally {
              setSavingPolicy(false);
            }
          }}
        />
      ) : null}
      {policyFormMode === "edit" && policy ? (
        <ScheduleAddPolicyForm
          key={policy.id}
          maxTenureMonths={editMaxTenureMonths}
          saving={savingPolicy}
          initialPolicy={policy}
          onCancel={() => setPolicyFormMode(null)}
          onSave={async (input) => {
            if (!customerId) {
              toast.error("Customer ID is required to update an insurance policy.");
              return;
            }
            setSavingPolicy(true);
            try {
              await onUpdatePolicy({ insuranceId: policy.id, customerId, ...input });
              setPolicyFormMode(null);
            } finally {
              setSavingPolicy(false);
            }
          }}
        />
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading insurance…
        </div>
      ) : policy && activeStatus ? (
        <div className="rounded-xl border bg-primary/5 px-4 py-4">
          <div className="flex flex-wrap items-start gap-x-5 gap-y-4 lg:items-center">
            <div className="flex min-w-[15rem] max-w-full flex-[1.4_1_15rem] items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Shield className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-tight">Active Insurance Policy</div>
                <div className="break-all text-sm font-semibold">{policy.policy_code || policy.insurance_provider || "—"}</div>
                {policy.policy_code && policy.insurance_provider ? (
                  <div className="break-words text-xs text-muted-foreground">{policy.insurance_provider}</div>
                ) : null}
              </div>
            </div>
            <div className="min-w-[9.5rem] flex-1">
              <div className="text-[11px] text-muted-foreground">Cover Period</div>
              <div className="text-sm font-medium leading-snug">{formatCoverPeriod(policy)}</div>
            </div>
            <div className="min-w-[5rem] flex-1">
              <div className="text-[11px] text-muted-foreground">Tenure</div>
              <div className="text-sm font-medium">{formatTenureLabel(policyTenureMonths(policy), "card")}</div>
            </div>
            <div className="min-w-[7rem] flex-1">
              <div className="text-[11px] text-muted-foreground">Total Sum Insured</div>
              <div className="text-sm font-medium tabular-nums">{inrFmt(scheduleSumInsured)}</div>
            </div>
            <div className="min-w-[7rem] flex-1">
              <div className="text-[11px] text-muted-foreground">Days to Expiry</div>
              {daysToExpiry != null && daysToExpiry >= 0 ? (
                <span className="mt-1 inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  {daysToExpiry} days
                </span>
              ) : (
                <div className="text-sm font-medium text-destructive">Expired</div>
              )}
            </div>
            <div className="min-w-[7.5rem] flex-1">
              <div className="text-[11px] text-muted-foreground">Insurance Expired On</div>
              <div className="text-sm font-medium">{formatPolicyDate(policy.end_date)}</div>
            </div>
            <div className="shrink-0 self-center">
              <InsuranceStatusChip status={activeStatus} />
            </div>
          </div>
        </div>
      ) : policies.length === 0 && !formOpen ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          No insurance policies added yet.
        </div>
      ) : !formOpen ? (
        <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          No active insurance policy for this rental schedule.
        </div>
      ) : null}

      {!loading && policy && policyFormMode !== "edit" ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="rounded-xl border bg-background p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Shield className="h-4 w-4 text-muted-foreground" />
              Insurer Details
            </div>
            <InsuranceDetailRow label="Insurer" value={policy.insurance_provider || "—"} />
            <InsuranceDetailRow label="Policy Number" value={policy.policy_code || "—"} />
          </div>
          <div className="rounded-xl border bg-background p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Contact className="h-4 w-4 text-muted-foreground" />
              Contact Details
            </div>
            <InsuranceDetailRow label="Coordinator Name" value={policy.coordinator_name || "—"} />
            <InsuranceDetailRow label="Phone" value={policy.coordinator_phone || "—"} />
            <InsuranceDetailRow label="Email" value={policy.coordinator_email || "—"} />
            <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
              <span className="shrink-0 text-muted-foreground">Remark</span>
              {policy.remarks ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="line-clamp-2 max-w-[70%] cursor-default text-right font-medium">
                        {policy.remarks}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap break-words">
                      {policy.remarks}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span className="text-right font-medium">—</span>
              )}
            </div>
          </div>
          <div className="rounded-xl border bg-background p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Documents
            </div>
            <div className="space-y-2">
              <InsuranceDocumentRow
                name={policy.upload_policy_s3_fileurl ? (policy.upload_policy_filename || "Policy document") : undefined}
                meta={documentDate !== "—" ? documentDate : undefined}
                emptyLabel="Policy not uploaded"
                accept=".pdf,application/pdf"
                busy={busyKey === "active-policy"}
                onPreview={policy.upload_policy_s3_fileurl
                  ? () => void runFileAction("active-policy", () => onPreviewFile(policy.upload_policy_s3_fileurl || "", policy.upload_policy_filename))
                  : undefined}
                onDownload={policy.upload_policy_s3_fileurl
                  ? () => void runFileAction("active-policy", () => onDownloadFile(policy.upload_policy_s3_fileurl || "", policy.upload_policy_filename))
                  : undefined}
                onUpload={(file) => void runFileAction("active-policy", () => onUploadDocument(policy.id, "policy", file))}
              />
              <InsuranceDocumentRow
                name={policy.upload_quotation_s3_fileurl ? (policy.upload_quotation_filename || "Quotation") : undefined}
                meta={documentDate !== "—" ? documentDate : undefined}
                emptyLabel="Quotation not uploaded"
                accept=".pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                busy={busyKey === "active-quotation"}
                onPreview={policy.upload_quotation_s3_fileurl
                  ? () => void runFileAction("active-quotation", () => onPreviewFile(policy.upload_quotation_s3_fileurl || "", policy.upload_quotation_filename))
                  : undefined}
                onDownload={policy.upload_quotation_s3_fileurl
                  ? () => void runFileAction("active-quotation", () => onDownloadFile(policy.upload_quotation_s3_fileurl || "", policy.upload_quotation_filename))
                  : undefined}
                onUpload={(file) => void runFileAction("active-quotation", () => onUploadDocument(policy.id, "quotation", file))}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border bg-background">
        <div className="border-b px-4 py-3 text-sm font-semibold">Policy History</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy No.</TableHead>
                <TableHead>Insurer</TableHead>
                <TableHead>Cover Period</TableHead>
                <TableHead>Tenure</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    Loading insurance…
                  </TableCell>
                </TableRow>
              ) : historyPolicies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No previous policies.
                  </TableCell>
                </TableRow>
              ) : historyPolicies.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm">{item.policy_code || "—"}</TableCell>
                    <TableCell className="text-sm">{item.insurance_provider || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatPolicyDate(item.start_date)} → {formatPolicyDate(item.end_date)}
                    </TableCell>
                    <TableCell className="text-sm">{formatTenureLabel(policyTenureMonths(item), "history")}</TableCell>
                    <TableCell>
                      <InsuranceStatusChip status="Expired" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 cursor-pointer"
                        aria-label="View previous policy"
                        onClick={() => setViewedPolicy(item)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={!!viewedPolicy} onOpenChange={(open) => { if (!open) setViewedPolicy(null); }}>
        <SheetContent title="Previous policy" className="overflow-y-auto">
          {viewedPolicy ? (
            <div className="space-y-4 pr-6">
              <SheetHeader className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <SheetTitle className="break-all text-base text-foreground">
                      {viewedPolicy.policy_code || "Previous policy"}
                    </SheetTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">Expired insurance policy</p>
                  </div>
                  <InsuranceStatusChip status="Expired" />
                </div>
              </SheetHeader>

              <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                  <Shield className="h-4 w-4" />
                  Policy Details
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <div className="text-[11px] text-muted-foreground">Insurer</div>
                    <div className="font-medium">{viewedPolicy.insurance_provider || "---"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Policy number</div>
                    <div className="break-all font-medium">{viewedPolicy.policy_code || "---"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] text-muted-foreground">Cover period</div>
                    <div className="font-medium">
                      {formatPolicyDate(viewedPolicy.start_date)} → {formatPolicyDate(viewedPolicy.end_date)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Tenure</div>
                    <div className="font-medium">{formatTenureLabel(policyTenureMonths(viewedPolicy), "history")}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Premium</div>
                    <div className="font-medium tabular-nums">{inrFmt(viewedPolicy.premium)}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border bg-background p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                  <Contact className="h-4 w-4" />
                  Coordinator
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Name</span>
                    <span className="text-right font-medium">{viewedPolicy.coordinator_name?.trim() || "---"}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Email</span>
                    <span className="break-all text-right font-medium">{viewedPolicy.coordinator_email?.trim() || "---"}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Phone</span>
                    <span className="text-right font-medium">{viewedPolicy.coordinator_phone?.trim() || "---"}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border bg-background p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                  <FileText className="h-4 w-4" />
                  Remarks
                </div>
                {viewedPolicy.remarks?.trim() ? (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="line-clamp-2 cursor-default text-sm font-medium">
                          {viewedPolicy.remarks}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap break-words">
                        {viewedPolicy.remarks}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <p className="text-sm font-medium">---</p>
                )}
              </div>

              <div className="rounded-xl border bg-background p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                  <Paperclip className="h-4 w-4" />
                  Documents
                </div>
                <div className="space-y-2">
                  <InsuranceDocumentRow
                    name={viewedPolicy.upload_policy_s3_fileurl ? (viewedPolicy.upload_policy_filename || "Policy document") : undefined}
                    emptyLabel="Policy not uploaded"
                    accept=".pdf,application/pdf"
                    busy={busyKey === `history-${viewedPolicy.id}-policy`}
                    onPreview={viewedPolicy.upload_policy_s3_fileurl
                      ? () => void runFileAction(`history-${viewedPolicy.id}-policy`, () => onPreviewFile(viewedPolicy.upload_policy_s3_fileurl || "", viewedPolicy.upload_policy_filename))
                      : undefined}
                    onDownload={viewedPolicy.upload_policy_s3_fileurl
                      ? () => void runFileAction(`history-${viewedPolicy.id}-policy`, () => onDownloadFile(viewedPolicy.upload_policy_s3_fileurl || "", viewedPolicy.upload_policy_filename))
                      : undefined}
                  />
                  <InsuranceDocumentRow
                    name={viewedPolicy.upload_quotation_s3_fileurl ? (viewedPolicy.upload_quotation_filename || "Quotation") : undefined}
                    emptyLabel="Quotation not uploaded"
                    accept=".pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    busy={busyKey === `history-${viewedPolicy.id}-quotation`}
                    onPreview={viewedPolicy.upload_quotation_s3_fileurl
                      ? () => void runFileAction(`history-${viewedPolicy.id}-quotation`, () => onPreviewFile(viewedPolicy.upload_quotation_s3_fileurl || "", viewedPolicy.upload_quotation_filename))
                      : undefined}
                    onDownload={viewedPolicy.upload_quotation_s3_fileurl
                      ? () => void runFileAction(`history-${viewedPolicy.id}-quotation`, () => onDownloadFile(viewedPolicy.upload_quotation_s3_fileurl || "", viewedPolicy.upload_quotation_filename))
                      : undefined}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SectionCard({ icon, title, done, counter, action, children }: {
  icon: React.ReactNode; title: string; done?: boolean; counter?: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${done ? "bg-emerald-500/10 text-emerald-700" : "bg-primary/10 text-primary"}`}>{icon}</div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {action}
          {counter && <span className="text-xs font-medium tabular-nums px-2 py-0.5 rounded-md bg-muted">{counter}</span>}
          {done && <Chip className="bg-emerald-500/10 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Chip>}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
